import { useEffect, useState } from 'react'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { SimplePoll } from './SimplePoll'
import { PieChartResults } from './PieChartResults'
import { EvaluationPoll } from './EvaluationPoll'
import { RadarChartResults } from './RadarChartResults'
import {
  EvaluationPoll as EvaluationPollType,
  PollMode,
  SimplePoll as SimplePollType,
} from './pollTypes'
import { debateToolModes } from '@/utils/common'
import { useMeetingAppContext } from '@/MeetingAppContextDef'
import { useHall } from '@/contexts/HallProvider'
import { SpeakingRole, WinType } from '@db8/shared'
import { usePoll } from '@/contexts/PollProvider'
import { useHallUsers } from '@/contexts/HallUsersProvider'

export const PollingModal = () => {
  const { hall, hallMetadata } = useHall()
  const { pollResults, showWinner } = usePoll()
  const { isSpectator, currentUser, spectatorUsersSansBot, affirmativeUsers, negativeUsers } =
    useHallUsers()

  const pollFlow: PollMode[] =
    hall.winType === WinType.SIMPLEPOLL
      ? ['simplePoll', 'pieChartResults']
      : ['evaluationPoll', 'radarChartResults']

  // * If spectator allowing voting, debater/judges can watch the results come in
  const [currentStep, setCurrentStep] = useState(0)
  const { setDebateToolMode, debateToolMode } = useMeetingAppContext()

  useEffect(() => {
    if (isSpectator) setCurrentStep(1)
  }, [isSpectator])

  const nextStep = () => {
    if (currentStep < pollFlow.length - 1) {
      setCurrentStep((prev) => prev + 1)
    } else {
      setDebateToolMode(null)
      setCurrentStep(0)
    }
  }

  useEffect(() => {
    if (!currentUser || !hall || !pollResults) return

    const isTeamMember = [...affirmativeUsers, ...negativeUsers].includes(currentUser.id)
    if (isTeamMember) {
      setCurrentStep(1)
      return
    }

    if (hall.winType === WinType.SIMPLEPOLL) {
      const simpleResults = pollResults as SimplePollType
      const affirmativeVotes = Array.isArray(simpleResults[SpeakingRole.Affirmative])
        ? simpleResults[SpeakingRole.Affirmative]
        : []
      const negativeVotes = Array.isArray(simpleResults[SpeakingRole.Negative])
        ? simpleResults[SpeakingRole.Negative]
        : []

      const hasVoted = [...affirmativeVotes, ...negativeVotes].some(
        (user) => user.clerkId === currentUser.id,
      )

      if (hasVoted) setCurrentStep(1)
    }

    if (hall.winType === WinType.EVALUATIONPOLL) {
      const evaluationResults = pollResults as EvaluationPollType<string>
      const hasVoted = evaluationResults.castVotes && evaluationResults.castVotes > 0

      if (hasVoted) setCurrentStep(1)
    }

    return () => {
      setCurrentStep(0)
    }
  }, [currentUser, hall, pollResults, affirmativeUsers, negativeUsers])

  const renderCurrentMode = () => {
    const props = { onNext: nextStep }

    switch (pollFlow[currentStep]) {
      case 'simplePoll':
        return <SimplePoll {...props} />
      case 'pieChartResults':
        return <PieChartResults {...props} />
      case 'evaluationPoll':
        return <EvaluationPoll {...props} />
      case 'radarChartResults':
        return <RadarChartResults {...props} />
      default:
        return null
    }
  }

  // * Open immediately on debate end, close automatically on debate reset
  useEffect(() => {
    if (!hallMetadata?.pollingStarted && debateToolMode === debateToolModes.POLL)
      setDebateToolMode(null)
  }, [hallMetadata?.pollingStarted, debateToolMode])

  return (
    <Dialog
      open={debateToolMode === debateToolModes.POLL && !showWinner.val}
      onOpenChange={(open) => {
        if (!open) setDebateToolMode(null)
      }}
    >
      <DialogContent
        size="md"
        className="dark:text-white max-h-[80vh] md:min-w-[600px] noScrollbar"
      >
        {renderCurrentMode()}
        {currentStep === 1 && pollResults.castVotes > 0 && (
          <p className="text-center text-lg font-semibold pt-3">
            {' '}
            {pollResults.castVotes}/{spectatorUsersSansBot.length} Votes Cast
          </p>
        )}
      </DialogContent>
    </Dialog>
  )
}
